// 🔧 social_connections.js
// ✅ Purpose: Event Settings page for managing Integration Engine social connections
// ✅ Features:
// - Reads event-scoped integrations from events/{eventId}/integrations
// - Lets entitled managers connect all four platforms through startIntegrationOAuth
// - Keeps connection availability separate from implemented publishing formats
// - Supports disconnecting saved provider records from Firestore
// - Shows connected account/page status
// - Uses standard LGV success/error notifications for 20 seconds
// - Keeps Promotion Engine decoupled from OAuth and token management
// - Fully block-commented for clarity and future devs

import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  httpsCallable,
} from "firebase/functions";
import { functions } from "../../firebase";
import { PROMOTION_ENABLED_PLATFORM_OPTIONS } from "../../promotion/constants/promotion_platforms";

import {
  useEventContext,
} from "../../hooks/use_event_context";

import {
  disconnectIntegration,
  getIntegrationConnectionMap,
} from "../../services/integration_connections";

import FullPageSpinner from "../../components/FullPageSpinner";

import {
  logger,
} from "../../utils/logger";

const SOCIAL_PROVIDERS = [
  {
    key: "facebook",
    label: "Facebook",
    icon: "📘",
    description:
      "Connect a Facebook Page for this event. Text publishing is supported; other formats can be saved as drafts.",
  },
  {
    key: "instagram",
    label: "Instagram",
    icon: "📸",
    description:
      "Connect an Instagram professional account for this event. Direct publishing is still being completed.",
  },
  {
    key: "linkedin",
    label: "LinkedIn",
    icon: "💼",
    description:
      "Connect a LinkedIn account for this event. Direct publishing is still being completed.",
  },
  {
    key: "tiktok",
    label: "TikTok",
    icon: "🎵",
    description:
      "Connect a TikTok account for this event. Direct publishing is still being completed.",
  },
].flatMap((provider) => {
  const option = PROMOTION_ENABLED_PLATFORM_OPTIONS.find((candidate) => candidate.value === provider.key);
  return option ? [{ ...provider, label: option.label, enabled: option.enabled, statusLabel: "Connection Available" }] : [];
});

const SocialConnections = () => {
  const {
    eventslug,
    eventId,
    eventDoc,
    ready,
    eventLoading,
  } = useEventContext({ redirectIfNotFound: true });

  const [connections, setConnections] = useState({});
  const [loadingConnections, setLoadingConnections] = useState(true);
  const [savingProvider, setSavingProvider] = useState("");
  const [status, setStatus] = useState(null);

  const connectedCount = useMemo(() => {
    return SOCIAL_PROVIDERS.filter(
      (provider) =>
        connections?.[provider.key]?.status === "connected" ||
        connections?.[provider.key]?.connected === true
    ).length;
  }, [connections]);

  useEffect(() => {
    if (!status?.message) {
      return undefined;
    }

    const timer = setTimeout(() => {
      setStatus(null);
    }, 20000);

    return () => clearTimeout(timer);
  }, [status]);

  useEffect(() => {
    if (!eventId) {
      return undefined;
    }

    let cancelled = false;
    setLoadingConnections(true);

    getIntegrationConnectionMap(eventId)
      .then((nextConnections) => {
        if (!cancelled) {
          setConnections(nextConnections);
          setLoadingConnections(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          logger.error("❌ Failed to load social integration", {
            value: err,
          });
          setStatus({
            type: "error",
            message: "Failed to load social connection status.",
          });
          setLoadingConnections(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [eventId]);

  const getConnectionDisplayName = (connection) => {
    if (!connection) {
      return "Not connected";
    }

    return (
      connection.selectedPageName ||
      connection.pageName ||
      connection.account?.name ||
      connection.accountName ||
      connection.name ||
      "Connected account"
    );
  };

  const handleConnect = async (providerKey) => {
    if (!eventId || !eventslug) {
      setStatus({
        type: "error",
        message:
          "Event context is missing. Please open Social Connections from an event page.",
      });

      return;
    }

    if (!SOCIAL_PROVIDERS.some((provider) => provider.key === providerKey && provider.enabled)) {
      setStatus({
        type: "warning",
        message:
          "Choose one of the available social platforms.",
      });

      return;
    }

    try {
      setSavingProvider(providerKey);

      logger.log("🔌 Starting Integration OAuth", {
        value: {
          eventId,
          eventslug,
          providerKey,
        },
      });

      const startIntegrationOAuth = httpsCallable(
        functions,
        "startIntegrationOAuth"
      );

      const result = await startIntegrationOAuth({
        provider: providerKey,
        platform: providerKey,
        eventId,
        eventslug,
        returnUrl: `/${eventslug}/integrations`,
      });

      const authUrl =
        result?.data?.authUrl ||
        result?.data?.url ||
        result?.data?.redirectUrl ||
        result?.data?.authorizationUrl ||
        "";

      if (result?.data?.setupRequired) {
        throw new Error(result.data.message || "This social platform needs administrator configuration before connecting.");
      }

      if (!authUrl) {
        throw new Error(
          "OAuth start did not return a redirect URL."
        );
      }

      window.location.assign(authUrl);
    } catch (err) {
      logger.error("❌ Failed to start Integration OAuth", {
        value: err,
      });

      setStatus({
        type: "error",
        message:
          err.message ||
          "Failed to start social connection. Please try again.",
      });
    } finally {
      setSavingProvider("");
    }
  };

  const handleDisconnect = async (providerKey) => {
    if (!eventId) {
      return;
    }

    const confirmed = window.confirm(
      `Disconnect ${providerKey} from this event?\n\nPromotion posts will no longer be able to publish to this provider until it is reconnected.`
    );

    if (!confirmed) {
      return;
    }

    try {
      setSavingProvider(providerKey);

      await disconnectIntegration({eventId, platform: providerKey});
      setConnections((current) => ({
        ...current,
        [providerKey]: current[providerKey]
          ? {...current[providerKey], connected: false}
          : null,
      }));

      setStatus({
        type: "warning",
        message: "Social connection disconnected.",
      });
    } catch (err) {
      logger.error("❌ Failed to disconnect social provider", {
        value: err,
      });

      setStatus({
        type: "error",
        message:
          err.message ||
          "Failed to disconnect this provider.",
      });
    } finally {
      setSavingProvider("");
    }
  };

  if (!ready || eventLoading || !eventId) {
    return (
      <FullPageSpinner message="Loading Social Connections..." />
    );
  }

  return (
    <section id="top-content" className="container-fluid py-5">
      <div
        className="container"
        style={{
          maxWidth: "1200px",
        }}
      >
        <div className="row mb-4">
          <div className="col-12 col-lg-8">
            <h1 className="display-5 mb-2">
              🔌 Social Connections
            </h1>

            <p className="lead text-muted mb-0">
              Connect social accounts used by the Promotion Engine.
            </p>

            {eventDoc?.name && (
              <div className="small text-muted mt-2">
                Event: {eventDoc.name}
              </div>
            )}
          </div>

          <div className="col-12 col-lg-4 mt-3 mt-lg-0">
            <div className="card shadow-sm h-100">
              <div className="card-body">
                <div className="text-muted small">
                  Connected Providers
                </div>

                <div className="fs-3 fw-bold">
                  {loadingConnections ? "..." : connectedCount}
                </div>

                <div className="small text-muted">
                  Facebook is the MVP publishing provider.
                </div>
              </div>
            </div>
          </div>
        </div>

        {status?.message && (
          <div
            className={`alert ${
              status.type === "success"
                ? "alert-success"
                : status.type === "warning"
                ? "alert-warning"
                : status.type === "error"
                ? "alert-danger"
                : "alert-info"
            }`}
            role="alert"
          >
            {status.message}
          </div>
        )}

        <div className="card shadow-sm mb-4">
          <div className="card-header bg-white">
            <strong>Integration Engine</strong>
          </div>

          <div className="card-body">
            <p className="text-muted mb-0">
              Social Connections are event-scoped. Promotion Engine creates,
              approves, schedules, and publishes content, but OAuth and external
              platform communication are owned by the Integration Engine.
            </p>
          </div>
        </div>

        <div className="row g-4">
          {SOCIAL_PROVIDERS.map((provider) => {
            const connection = connections?.[provider.key];
            const connected = connection?.status === "connected";
            const saving = savingProvider === provider.key;

            return (
              <div
                key={provider.key}
                className="col-12 col-md-6"
              >
                <div className="card h-100 shadow-sm">
                  <div className="card-body d-flex flex-column">
                    <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
                      <div>
                        <h2 className="h5 mb-1">
                          {provider.icon} {provider.label}
                        </h2>

                        <p className="text-muted mb-0">
                          {provider.description}
                        </p>
                      </div>

                      <span
                        className={`badge ${
                          connected
                            ? "bg-success"
                            : provider.enabled
                            ? "bg-primary"
                            : "bg-light text-dark border"
                        }`}
                      >
                        {connected
                          ? "Connected"
                          : provider.statusLabel}
                      </span>
                    </div>

                    <div className="border rounded bg-light p-3 mb-3">
                      <div className="small text-muted">
                        Account / Page
                      </div>

                      <div className="fw-bold">
                        {loadingConnections
                          ? "Loading..."
                          : getConnectionDisplayName(connection)}
                      </div>

                      {connection?.connectedAt && (
                        <div className="small text-muted mt-1">
                          Connected At:{" "}
                          {connection.connectedAt?.toDate
                            ? connection.connectedAt.toDate().toLocaleString()
                            : String(connection.connectedAt)}
                        </div>
                      )}
                    </div>

                    <div className="mt-auto d-flex flex-wrap gap-2">
                      {connected ? (
                        <>
                          <button
                            type="button"
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => handleConnect(provider.key)}
                            disabled={!provider.enabled || saving}
                          >
                            {saving ? "Starting..." : "Reconnect"}
                          </button>

                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => handleDisconnect(provider.key)}
                            disabled={saving}
                          >
                            {saving ? "Disconnecting..." : "Disconnect"}
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          className={
                            provider.enabled
                              ? "btn btn-primary btn-sm"
                              : "btn btn-outline-secondary btn-sm"
                          }
                          onClick={() => handleConnect(provider.key)}
                          disabled={!provider.enabled || saving}
                        >
                          {provider.enabled
                            ? saving
                              ? "Starting..."
                              : `Connect ${provider.label}`
                            : "Coming Soon"}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="card shadow-sm mt-4">
          <div className="card-header bg-white">
            <strong>T-10 Status</strong>
          </div>

          <div className="card-body">
            <div className="row g-3">
              <div className="col-12 col-md">
                <div className="border rounded p-3 h-100 text-center">
                  <div className="fw-bold">
                    1. Connect
                  </div>
                  <div className="small text-muted">
                    Start OAuth
                  </div>
                </div>
              </div>

              <div className="col-12 col-md">
                <div className="border rounded p-3 h-100 text-center">
                  <div className="fw-bold">
                    2. Return
                  </div>
                  <div className="small text-muted">
                    OAuth callback
                  </div>
                </div>
              </div>

              <div className="col-12 col-md">
                <div className="border rounded p-3 h-100 text-center">
                  <div className="fw-bold">
                    3. Save
                  </div>
                  <div className="small text-muted">
                    Event integration
                  </div>
                </div>
              </div>

              <div className="col-12 col-md">
                <div className="border rounded p-3 h-100 text-center">
                  <div className="fw-bold">
                    4. Publish
                  </div>
                  <div className="small text-muted">
                    Promotion Engine
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SocialConnections;
